import React, { useEffect, useMemo, useState } from 'react';
import {
  computeV2HourlyDistribution,
  computeBrowserFleetSizing,
  SLOTS_PER_REPLICA_TYPICAL,
} from '../utils/massFollowV2Preview';
import { api } from '../services/api';

const BAR_MAX_PX = 72;

function barClass(band) {
  if (band === 'day') return 'bg-purple-500 dark:bg-purple-500';
  if (band === 'evening') return 'bg-purple-400/90 dark:bg-purple-600';
  return 'bg-slate-400 dark:bg-slate-500';
}

/**
 * Rough 24h estimated follow volume preview for V2 tasks (humanized curve; not exact runtime).
 * Includes peak-hour browser slot / replica demand vs live login-worker fleet.
 */
export default function V2SendDistributionPreview({
  accountCount,
  dailyMin,
  dailyMax,
  activeHoursPerDay = 12,
}) {
  const dist = useMemo(
    () => computeV2HourlyDistribution(accountCount, dailyMin, dailyMax, activeHoursPerDay),
    [accountCount, dailyMin, dailyMax, activeHoursPerDay]
  );

  const fleetSizing = useMemo(() => computeBrowserFleetSizing(dist.hours), [dist.hours]);

  const [fleetLive, setFleetLive] = useState(null);
  const [fleetError, setFleetError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setFleetError(null);
    api
      .get('/mass-follow/browser-follow-capacity')
      .then((res) => {
        if (!cancelled) setFleetLive(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setFleetLive(null);
          setFleetError(err?.response?.data?.error || err?.message || 'unavailable');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { hours, totalMin, totalMax } = dist;

  /** Log-scaled heights so taller bars reflect larger absolute hourly volume when account count grows (not just shape). */
  const barHeights = useMemo(() => {
    const maxH = Math.max(...hours.map((row) => Math.log10(1 + Math.max(0, row.mid))), 1e-6);
    return hours.map((row) => {
      const t = Math.log10(1 + Math.max(0, row.mid)) / maxH;
      return Math.max(4, Math.round(t * BAR_MAX_PX));
    });
  }, [hours]);

  if (accountCount <= 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-600 p-4 text-sm text-gray-500 dark:text-gray-400">
        Select one or more accounts to see an estimated 24-hour distribution preview.
      </div>
    );
  }

  const totalSlots = fleetLive?.totalSlots;
  const replicaCount = fleetLive?.replicaCount;
  const slotsPerReplicaApprox = fleetLive?.slotsPerReplicaApprox;

  let fleetVsPeak = null;
  if (fleetSizing && typeof totalSlots === 'number') {
    const needMax = fleetSizing.slotsNeeded.max;
    const needMin = fleetSizing.slotsNeeded.min;
    if (totalSlots >= needMax) fleetVsPeak = 'ok';
    else if (totalSlots >= needMin) fleetVsPeak = 'tight';
    else fleetVsPeak = 'low';
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3">
      <div>
        <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
          24h follow distribution (preview)
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {accountCount.toLocaleString()} selected accounts · estimated follows per local hour (humanized:           quieter at night,
          busier by day). Bar height uses a log scale of estimated follows so volume grows visibly as you add accounts; actual gaps are randomized per run.
        </div>
      </div>

      <div className="flex items-end gap-0.5 sm:gap-1 overflow-x-auto pb-1 pt-1 min-h-[100px]">
        {hours.map((row, i) => {
          const px = barHeights[i];
          const title = `Hour ${row.label}:00 — about ${row.estMin.toLocaleString()}–${row.estMax.toLocaleString()} follows (from daily min/max)`;
          return (
            <div key={row.hour} className="flex flex-col items-center flex-1 min-w-[22px] max-w-[36px]" title={title}>
              <div
                className={`w-full rounded-t transition-all ${barClass(row.band)}`}
                style={{ height: `${px}px`, minHeight: '4px' }}
              />
              <div className="text-[8px] sm:text-[9px] text-gray-500 dark:text-gray-400 mt-1 tabular-nums leading-none">
                {row.label}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600 dark:text-gray-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-slate-400 dark:bg-slate-500" />
          Quiet (night)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-purple-500" />
          Peak (day)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-purple-400 dark:bg-purple-600" />
          Evening
        </span>
      </div>

      <div className="text-xs text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-2 space-y-2">
        <div>
          <span className="font-medium text-gray-700 dark:text-gray-300">Total volume (all accounts): </span>
          {totalMin.toLocaleString()}–{totalMax.toLocaleString()} follows/day
        </div>

        {fleetSizing && (
          <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700 p-2.5 space-y-1.5">
            <div className="font-medium text-gray-800 dark:text-gray-200 text-xs">
              Peak-hour load &amp; browser fleet (estimate)
            </div>
            <p className="text-[11px] leading-snug text-gray-600 dark:text-gray-400">
              Busiest hour ~{fleetSizing.peakHourLabel}:00 local — up to{' '}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                {fleetSizing.peakEstMax.toLocaleString()}
              </span>{' '}
              follows (upper bound from your daily max curve). At ~{fleetSizing.secPerFollowRange}s per follow per browser,
              each slot can do about {fleetSizing.followsPerSlotPerHour.min}–{fleetSizing.followsPerSlotPerHour.max}{' '}
              follows/hour.
            </p>
            <p className="text-[11px] leading-snug text-gray-700 dark:text-gray-300">
              <span className="font-medium">Suggested capacity (peak):</span>{' '}
              ~{fleetSizing.slotsNeeded.min}–{fleetSizing.slotsNeeded.max} concurrent browser slots · ~{fleetSizing.replicasNeeded.min}–
              {fleetSizing.replicasNeeded.max} login-worker replicas at {fleetSizing.slotsPerReplica} slots each (
              {SLOTS_PER_REPLICA_TYPICAL} is typical).
            </p>
            {typeof totalSlots === 'number' && (
              <p className="text-[11px] leading-snug">
                <span className="font-medium text-gray-800 dark:text-gray-200">Live fleet (Redis):</span>{' '}
                {totalSlots} total slot{totalSlots !== 1 ? 's' : ''}
                {typeof replicaCount === 'number' ? (
                  <>
                    {' '}
                    across {replicaCount} replica{replicaCount !== 1 ? 's' : ''}
                    {typeof slotsPerReplicaApprox === 'number'
                      ? ` (~${slotsPerReplicaApprox} slots/replica)`
                      : ''}
                  </>
                ) : null}
                .
                {fleetVsPeak === 'ok' && (
                  <span className="text-green-700 dark:text-green-400"> Peak-hour estimate is within fleet throughput.</span>
                )}
                {fleetVsPeak === 'tight' && (
                  <span className="text-amber-700 dark:text-amber-400">
                    {' '}
                    Tight: fleet may be enough at faster follow times; consider more slots if you see backlog.
                  </span>
                )}
                {fleetVsPeak === 'low' && (
                  <span className="text-red-700 dark:text-red-400">
                    {' '}
                    Below suggested peak slots — daily caps may take longer to finish or queue during busy hours.
                  </span>
                )}
              </p>
            )}
            {fleetError && !fleetLive && (
              <p className="text-[11px] text-amber-700 dark:text-amber-400">
                Could not load live fleet size ({fleetError}). Suggested replica counts still apply; refresh to compare.
              </p>
            )}
          </div>
        )}

        <div className="text-gray-500 dark:text-gray-500 text-[11px]">
          Browser: Camoufox · Task scheduling is slot-aware; per-account delays stay within daily caps.
        </div>
      </div>
    </div>
  );
}
